import React, { useEffect, useState } from 'react';
import './RoomPill.css';

/**
 * Reusable room indicator pill with a pulsing dot.
 *
 * Props:
 *  - code:  the room code/handle to render after the prefix label
 *  - kind:  'global' | 'party' — drives label prefix + dot color
 *  - size:  'sm' | 'md' — sm is used in MobileNav, md in sidebar/header rows
 *
 * The pulsing dot pauses when `document.visibilityState === 'hidden'`
 * (NFR for battery + perceived performance — no point animating an
 * offscreen tab) and is rendered static under `prefers-reduced-motion`
 * via a CSS media query in RoomPill.css.
 */
const RoomPill = ({
  code = 'GLOB',
  kind = 'global',
  size = 'sm',
  className = '',
  count,
}) => {
  const [paused, setPaused] = useState(
    typeof document !== 'undefined' && document.visibilityState === 'hidden'
  );

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVis = () => setPaused(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const prefix = kind === 'party' ? 'Party' : 'Global';
  const composed = `room-pill room-pill--${size} room-pill--${kind}${
    paused ? ' room-pill--paused' : ''
  }${className ? ` ${className}` : ''}`;

  return (
    <span className={composed} data-kind={kind}>
      <span className="room-pill__dot" aria-hidden="true" />
      <span className="room-pill__label">
        {prefix}
        {' · '}
        <b className="room-pill__code">{code}</b>
        {typeof count === 'number' && count > 0 && (
          <>
            {' · '}
            <span className="room-pill__count">{count} online</span>
          </>
        )}
      </span>
    </span>
  );
};

export default RoomPill;
